import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';
import type {
	PeriodTemplate,
	TimetableSlot,
} from './masterAdminTimetableApi';

export const schoolAdminTimetableApi = createApi({
	reducerPath: 'schoolAdminTimetableApi',
	baseQuery: axiosBaseQuery,
	tagTypes: ['SaPeriodTemplates', 'SaTimetableSlots'],
	endpoints: (builder) => ({
		saListPeriodTemplates: builder.query<
			{ templates: PeriodTemplate[] },
			void
		>({
			query: () => ({ url: '/school-admin/timetable/period-templates' }),
			providesTags: ['SaPeriodTemplates'],
		}),
		saCreatePeriodTemplate: builder.mutation<
			PeriodTemplate,
			{
				name: string;
				periodNumber: number;
				startTime: string;
				endTime: string;
				daysOfWeek: number[];
			}
		>({
			query: (body) => ({
				url: '/school-admin/timetable/period-templates',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['SaPeriodTemplates'],
		}),
		saUpdatePeriodTemplate: builder.mutation<
			PeriodTemplate,
			{ id: string; updates: Partial<PeriodTemplate> }
		>({
			query: ({ id, updates }) => ({
				url: `/school-admin/timetable/period-templates/${id}`,
				method: 'PUT',
				data: updates,
			}),
			invalidatesTags: ['SaPeriodTemplates'],
		}),
		saDeletePeriodTemplate: builder.mutation<{ message: string }, string>({
			query: (id) => ({
				url: `/school-admin/timetable/period-templates/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['SaPeriodTemplates'],
		}),
		saListSlots: builder.query<
			{
				weekStartDate?: string;
				startDate?: string;
				endDate?: string;
				timezone: string;
				slots: TimetableSlot[];
			},
			{ weekStartDate?: string; startDate?: string; endDate?: string }
		>({
			query: ({ weekStartDate, startDate, endDate }) => ({
				url: '/school-admin/timetable/slots',
				params: { weekStartDate, startDate, endDate },
			}),
			providesTags: ['SaTimetableSlots'],
		}),
		saBulkCreateSlots: builder.mutation<
			{ created: number; skipped: number; weekStartDate: string },
			{ weekStartDate: string; classIds: string[]; templateIds: string[] }
		>({
			query: (body) => ({
				url: '/school-admin/timetable/slots/bulk',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['SaTimetableSlots'],
		}),
		saUpdateSlot: builder.mutation<
			TimetableSlot,
			{ id: string; updates: { isLabSlot?: boolean } }
		>({
			query: ({ id, updates }) => ({
				url: `/school-admin/timetable/slots/${id}`,
				method: 'PATCH',
				data: updates,
			}),
			invalidatesTags: ['SaTimetableSlots'],
		}),
		saDeleteSlot: builder.mutation<
			{ message: string; deletedCount: number; scope: 'single' | 'series' },
			{ id: string; scope?: 'single' | 'series' } | string
		>({
			query: (arg) => {
				const id = typeof arg === 'string' ? arg : arg.id;
				const scope =
					typeof arg === 'string' ? 'single' : arg.scope || 'single';
				return {
					url: `/school-admin/timetable/slots/${id}`,
					method: 'DELETE',
					params: { scope },
				};
			},
			invalidatesTags: ['SaTimetableSlots'],
		}),
		saPublishWeek: builder.mutation<
			{ published: number; weekStartDate: string },
			{ weekStartDate: string }
		>({
			query: (body) => ({
				url: '/school-admin/timetable/slots/publish',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['SaTimetableSlots'],
		}),
		saWizardGenerateSlots: builder.mutation<
			{ created: number; skipped: number; totalDays: number },
			{
				classIds: string[];
				cadence: '1x' | '2x' | '3x' | 'daily' | 'custom';
				customDays?: number[];
				startTime: string;
				endTime: string;
				startDate: string;
				endDate: string;
				isLabSlot: boolean;
				combineClasses?: boolean;
			}
		>({
			query: (body) => ({
				url: '/school-admin/timetable/slots/wizard-generate',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['SaTimetableSlots'],
		}),
	}),
});

export const {
	useSaListPeriodTemplatesQuery,
	useSaCreatePeriodTemplateMutation,
	useSaUpdatePeriodTemplateMutation,
	useSaDeletePeriodTemplateMutation,
	useSaListSlotsQuery,
	useSaBulkCreateSlotsMutation,
	useSaUpdateSlotMutation,
	useSaDeleteSlotMutation,
	useSaPublishWeekMutation,
	useSaWizardGenerateSlotsMutation,
} = schoolAdminTimetableApi;
